import * as truckActions from 'actions/truck.js'
import { TRUCK } from 'utils/constants'
import { fromJS, List, Map } from 'immutable'
import { setIn } from 'utils/immutable'
import { compose, isEmpty, propOr, pathOr } from 'ramda'
import { getPrimaryModuleName } from 'utils/circuits.js'
import { createReducer } from './utils'

export const initialState = fromJS({
  trucksToCompare: [],
  [TRUCK]: {},
  serviceRecordModal: {
    visible: false,
    cancelHandler: () => {},
  },
  rpPinAssignmentModalVisible: false,
})

function setDataOnTruck(trucks, serialNumber, data) {
  const truck = trucks.get(serialNumber)
  let newTruck = {}

  if (!truck) {
    newTruck = fromJS({
      referenceParameters: [],
      stagedReferenceParameters: [],
      vehicleDetailsLoading: false,
      vehicleFleetLoading: false,
      dashConfigLoading: false,
      vehicleRevisionsLoading: false,
      fleetSize: 0,
      dashConfig: null,
      vehicleType: null,
      modules: [],
      switchLinkedLogic: {},
      parFileValidation: {},
      ...data,
    })
  } else {
    newTruck = truck.merge(data)
  }
  return trucks.set(serialNumber, newTruck)
}

const handlers = {
  [truckActions.TOGGLE_SELECTED_TRUCK]: toggleSelectedTruck,
  [truckActions.CLEAR_SELECTED_TRUCKS]: clearSelectedTrucks,
  [truckActions.REVERT_ALL_COMPARISON_CHANGES]: revertAllComparisonChanges,
  [truckActions.STAGE_REFERENCE_PARAMETER_ON_VEHICLE]:
    stageReferenceParameterOnVehicle, // TODO: being used anymore?
  [truckActions.CLEAR_STAGED_REFERENCE_PARAMETERS_ON_VEHICLE]:
    clearStagedReferenceParametersOnVehicle,
  [truckActions.REVERT_VEHICLE_CHANGES]: revertVehicleChanges,
  [truckActions.SET_LOADING_STATUS]: setVehicleLoadingStatus,
  // TODO: truck -> vehicle
  [truckActions.VEHICLE_FEATURES_RECEIVED]: truckFeaturesReceived,
  [truckActions.TRUCK_FEATURES_UPDATED]: truckFeaturesReceived,
  [truckActions.TRUCK_MODULES_LOADING]: truckModulesLoading,
  [truckActions.TRUCK_MODULES_RECEIVED]: truckModulesReceived,
  [truckActions.TRUCK_MODULES_FAILURE]: truckModulesFailed,
  [truckActions.REQUESTING_TRUCK_REFERENCE_PARAMETERS]:
    requestingTruckReferenceParameters,
  [truckActions.GET_VEHICLE_REFERENCE_PARAMETERS]:
    getVehicleReferenceParameters,
  // Failure handler for both the legacy thunk path (TRUCK_REFERENCE_PARAMETERS_NOT_RECEIVED
  // was removed when the thunk was migrated to apiAction) and the current apiAction path.
  [truckActions.GET_VEHICLE_REFERENCE_PARAMETERS_FAILURE]:
    truckReferenceParametersNotReceived,
  [truckActions.REQUESTING_TRUCK_DETAILS]: requestingTruckDetails,
  [truckActions.GET_TRUCK_DETAILS]: getTruckDetails,
  [truckActions.TRUCK_DETAILS_NOT_RECEIVED]: truckDetailsNotReceived,
  [truckActions.TRUCK_DETAILS_RECEIVED]: truckDetailsReceived,
  [truckActions.SAVING_VEHICLE_REFERENCE_PARAMETERS]:
    savingVehicleReferenceParameters,
  [truckActions.VEHICLE_REFERENCE_PARAMETERS_SAVED]:
    vehicleReferenceParametersSaved,
  [truckActions.VEHICLE_REFERENCE_PARAMETERS_SAVE_FAILED]:
    vehicleReferenceParametersSaveFailed,
  [truckActions.TOGGLE_SELECTED_STAGED_REFERENCE_PARAMETER]:
    toggleSelectedStagedReferenceParameter,
  [truckActions.REQUESTING_VEHICLE_FLEET_SIZE]: requestingVehicleFleetSize,
  [truckActions.VEHICLE_FLEET_SIZE_RECEIVED]: vehicleFleetSizeReceived,
  // Clear vehicleFleetLoading on failure — without this, the fleet spinner
  // stays permanently visible when requestVehicleFleetSize fails, because
  // REQUESTING_VEHICLE_FLEET_SIZE sets vehicleFleetLoading: true and nothing
  // else clears it on the failure path.
  [truckActions.VEHICLE_FLEET_NOT_RECEIVED]: vehicleFleetNotReceived,
  [truckActions.SET_SERVICE_RECORD_MODAL]: settingServiceRecordModalVisibility,
  [truckActions.SHOW_RP_PIN_ASSIGNMENT_MODAL]: showRpPinAssignmentModal,
  [truckActions.HIDE_RP_PIN_ASSIGNMENT_MODAL]: hideRpPinAssignmentModal,
  [truckActions.REQUESTING_VEHICLE_REVISIONS]: requestingVehicleRevisions,
  [truckActions.VEHICLE_REVISIONS_RECEIVED]: vehicleRevisionsReceived,
  [truckActions.VEHICLE_REVISIONS_NOT_RECEIVED]: vehicleRevisionsNotReceived,
  [truckActions.GET_VEHICLE_REVISIONS]: getVehicleRevisions,
  [truckActions.VALIDATE_PAR_FILE_SUCCESS]: getParFileValidationData,
  [truckActions.GET_VEHICLE_COMMENTS_RECEIVED]: vehicleCommentsReceived,
  [truckActions.ADD_VEHICLE_COMMENT_SUCCESS]: addVehicleCommentSuccess,
  [truckActions.SET_VEHICLE_TEMP_COMMENT]: setVehicleTempComment,
}

export default createReducer(initialState, handlers)

function toggleSelectedTruck(state, action) {
  const trucksToCompare = state.get('trucksToCompare')
  const { serialNumber } = action
  const index = trucksToCompare.findIndex((tId) => tId === serialNumber)
  const newTrucksToCompare =
    index > -1
      ? trucksToCompare.delete(index)
      : trucksToCompare.push(serialNumber)

  return state.set('trucksToCompare', newTrucksToCompare)
}

function clearSelectedTrucks(state, action) {
  return state.set('trucksToCompare', List([]))
}

function revertAllComparisonChanges(state, action) {
  const trucks = state.get(TRUCK)

  return state.merge({
    [TRUCK]: trucks.map((t) => t.set('stagedReferenceParameters', List([]))),
  })
}

function stageReferenceParameterOnVehicle(state, action) {
  const {
    serialNumber,
    rp: {
      uri,
      serviceDescription,
      refParameterNumber,
      masterNumber,
      status,
      release,
    },
  } = action
  const trucks = state.get(TRUCK)
  const truck = trucks.get(serialNumber)
  const stagedReferenceParameters = truck.get('stagedReferenceParameters')

  if (stagedReferenceParameters.find((tRp) => tRp.get('uri') === uri))
    return state

  const newStagedReferenceParameters = stagedReferenceParameters.push(
    Map({
      serviceDescription: serviceDescription || '',
      refParameterNumber,
      uri,
      key: `${masterNumber}-${refParameterNumber}-${release}`,
      status,
      selected: false,
      release,
    })
  )

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      stagedReferenceParameters: newStagedReferenceParameters,
    })
  )
}

function clearStagedReferenceParametersOnVehicle(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)
  const stagedReferenceParameters = []

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      stagedReferenceParameters,
    })
  )
}

function revertVehicleChanges(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      stagedReferenceParameters: [],
    })
  )
}

function setVehicleLoadingStatus(state, action) {
  const {
    serialNumber,
    isLoading = false,
    errorMessage,
    actionArguments,
  } = action
  const setTruckLoading = setIn(
    [TRUCK, serialNumber || actionArguments.serialNumber, 'isLoading'],
    errorMessage ? false : isLoading
  )

  return compose(setTruckLoading)(state)
}

function truckFeaturesReceived(state, { data: features, serialNumber }) {
  const setTruckLoadingFalse = setIn([TRUCK, serialNumber, 'isLoading'], false)

  const featurelessTruck = isEmpty(features)
  const newFeatures = featurelessTruck ? [] : features

  const setTruckFeatures = setIn([TRUCK, serialNumber, 'features'], newFeatures)

  return compose(setTruckLoadingFalse, setTruckFeatures)(state)
}

function truckModulesLoading(state, action) {
  const { serialNumber } = action
  return state.setIn([TRUCK, serialNumber, 'isLoading'], true)
}

function truckModulesReceived(state, action) {
  // The API returns the modules as an array directly in response.data (not
  // wrapped as { modules: [...] }). apiCall.js places response.data in
  // action.data, so action.data is the modules array itself.
  //
  // Fallback chain:
  //   1. action.data is an array  → use it directly (new apiAction path)
  //   2. action.data?.modules      → wrapped shape, kept for safety
  //   3. action.modules            → old hand-shaped thunk action (transition)
  //   4. []                        → no data, treat as moduleless
  const { serialNumber } = action
  const modules = Array.isArray(action.data)
    ? action.data
    : action.data?.modules || action.modules || []

  // Warn in development when the legacy fallback runs so incomplete migrations
  // are visible and can be cleaned up.
  if (process.env.NODE_ENV !== 'production' && !Array.isArray(action.data)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[truckModulesReceived] Legacy action shape detected — expected action.data to be an array:',
      action.type
    )
  }

  const total = modules.length

  const modulelessTruck = total === 0
  const newModules = modulelessTruck
    ? []
    : modules.filter((module) => module.type === 'ecu')

  return state
    .setIn([TRUCK, serialNumber, 'modules'], fromJS(newModules))
    .setIn([TRUCK, serialNumber, 'isLoading'], false)
}

function truckModulesFailed(state, action) {
  const { serialNumber, args } = action
  const sn = serialNumber || args?.serialNumber
  return state.setIn([TRUCK, sn, 'isLoading'], false)
}

function requestingTruckReferenceParameters(state, action) {
  // action.serialNumber is spread from args by apiCall.js — action.truck is
  // not present in the generic request action. We only need to set isLoading.
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, { isLoading: true })
  )
}

function getVehicleReferenceParameters(state, action) {
  const { serialNumber } = action

  // The new apiAction path sends the raw RP array directly in action.data.
  // The deprecated /reference-parameters/search POST wraps it as { data: { referenceParameters: [] } }.
  // Guard: if action.data is already an array use it directly; otherwise
  // fall back to the nested path for the deprecated endpoint, with [] as
  // the default (NOT the string 'referenceParameters' which was previously
  // the pathOr default — that caused .map() to iterate characters).
  const referenceParameters = Array.isArray(action.data)
    ? action.data
    : pathOr([], ['data', 'referenceParameters'], action)
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      referenceParameters: referenceParameters.map((rp) => ({
        ...rp,
        selected: false,
        key: `${rp.masterNumber}-${rp.refParameterNumber}-${rp.release}`,
      })),
      stagedReferenceParameters: [],
      isLoading: false,
      serialNumber,
      id: serialNumber,
    })
  )
}

function truckReferenceParametersNotReceived(state, action) {
  const { serialNumber, error } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      error,
      isLoading: false,
    })
  )
}

function requestingTruckDetails(state, action) {
  const { truck, serialNumber } = action
  const trucks = state.get(TRUCK)

  // TODO: This really shouldn't be doing an object assign. It does this to get the truck state from action.truck. We should have a function to generate a
  // truck based on the input action.
  return state.set(
    TRUCK,
    setDataOnTruck(trucks, propOr(serialNumber, 'serialNumber', truck), {
      vehicleDetailsLoading: true,
      vehicleDetailsError: false,
    })
  )
}

function getTruckDetails(state, { data }) {
  // todo switch back to { vehicle }
  const { serialNumber } = data
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      ...data, // TBD: primaryModule N/A from CEEA+ API
      primaryModule: getPrimaryModuleName(data.primaryModule),
      vehicleDetailsLoading: false,
    })
  )
}

function truckDetailsNotReceived(state, action) {
  const serialNumber =
    action.serialNumber ||
    (action.actionArguments || action.args || {}).serialNumber

  if (!serialNumber) return state

  const trucks = state.get(TRUCK)

  // Preserve the structured error so ErrorBoundary can render the correct
  // page (401 for auth failures, 404 for missing vehicles, etc.). Normalize
  // absent or unsupported codes to 0 which maps to the generic DefaultError.
  const SUPPORTED_CODES = [401, 404, 500, 504]
  const rawCode = action.code
  const normalizedCode = SUPPORTED_CODES.includes(rawCode) ? rawCode : 0
  const vehicleDetailsError = {
    code: normalizedCode,
    errorMessage: action.errorMessage || action.description || null,
    description: action.description || null,
    stack: action.stack || null,
  }

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleDetailsLoading: false,
      vehicleDetailsError,
    })
  )
}

function truckDetailsReceived(state, action) {
  const {
    serialNumber,
    data: { vehicle: vehicleDetails },
  } = action
  const trucks = state.get(TRUCK)
  //
  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      ...vehicleDetails, // TBD: primaryModule N/A from CEEA+ API
      primaryModule: getPrimaryModuleName(vehicleDetails.primaryModule),
      vehicleDetailsLoading: false,
    })
  )
}

function vehicleCommentsReceived(state, action) {
  const { serialNumber, data } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(
      trucks,
      serialNumber,
      Map({
        comments: List(data),
        vehicleDetailsLoading: false,
      })
    )
  )
}

function addVehicleCommentSuccess(state, action) {
  const { serialNumber, data } = action
  const trucks = state.get(TRUCK)
  const revisions = state.getIn(['vehicles', serialNumber, 'comments'])

  return state.set(
    TRUCK,
    setDataOnTruck(
      trucks,
      serialNumber,
      Map({
        comments: List([...revisions, Map(data)]),
      })
    )
  )
}

function setVehicleTempComment(state, action) {
  const { serialNumber, commentString } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(
      trucks,
      serialNumber,
      Map({
        tempComment: commentString,
      })
    )
  )
}

function savingVehicleReferenceParameters(state, action) {
  // action.serialNumber is spread from the apiAction args; action.vehicle is
  // not present in the generic request action produced by apiCall.js.
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      isLoading: true,
    })
  )
}

function vehicleReferenceParametersSaved(state, action) {
  // action.data is the RP array returned by the API. The mock API for
  // vehicles/${serialNumber}/reference-parameters/add returns { data: [...] }
  // with no `result` field, so apiCall.js places response.data (the array) into
  // action.data. If the real API ever adds a `result` wrapper on success,
  // apiCall.js would take its spread branch and action.data would become the
  // array directly. Either way, action.data should be the RP array.
  //
  // Note: the post-save saga (saveReferenceParameters.js) also dispatches
  // getTruckDetails which re-fetches and overwrites the RP list, so any
  // transient shape mismatch here is corrected within the same interaction.
  const { serialNumber, data: referenceParameters } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      referenceParameters,
      stagedReferenceParameters: [],
      isLoading: false,
    })
  )
}

function vehicleReferenceParametersSaveFailed(state, action) {
  const { serialNumber, error } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      error,
      isLoading: false,
    })
  )
}

function toggleSelectedStagedReferenceParameter(state, action) {
  const { serialNumber, referenceParameterUri } = action
  const trucks = state.get(TRUCK)
  const truck = trucks.get(serialNumber)

  const rpIdx = truck
    .getIn(['stagedReferenceParameters'])
    .findIndex((rp) => rp.get('uri') === referenceParameterUri)

  if (rpIdx < 0) return state

  const selected = truck.getIn(['stagedReferenceParameters', rpIdx, 'selected'])
  return state.set(
    TRUCK,
    trucks.setIn(
      [serialNumber, 'stagedReferenceParameters', rpIdx, 'selected'],
      !selected
    )
  )
}

function requestingVehicleFleetSize(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleFleetLoading: true,
    })
  )
}

function vehicleFleetSizeReceived(state, action) {
  // The generic apiCall success action wraps the API response in action.data.
  // The real API returns an array of vehicle objects; the mock returns
  // { fleet: string, vehicles: [...] }. fleetSize is derived from whichever
  // shape is present. Fall back to action.fleetSize for legacy direct dispatches.
  //
  // The ?? 0 final fallback is intentional: it matches the default initialState
  // value (fleetSize: 0) so existing components that read fleetSize as a number
  // don't need null guards. A response shape change that removes vehicles would
  // produce 0 rather than undefined/NaN — consistent with "no fleet members."
  const { serialNumber } = action
  // action.data can be either:
  //   Array  — real API returns the fleet vehicles directly as an array
  //   Object — mock/legacy shape { fleet: string, vehicles: [...] }
  const fleetSize =
    action.fleetSize ??
    (Array.isArray(action.data)
      ? action.data.length
      : action.data?.vehicles?.length) ??
    0

  // Warn in development when the legacy fallback runs (action.fleetSize set
  // directly rather than via action.data.vehicles) so the migration can be
  // confirmed complete.
  if (process.env.NODE_ENV !== 'production' && action.fleetSize !== undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      '[vehicleFleetSizeReceived] Legacy action shape detected — action.fleetSize set directly:',
      action.type
    )
  }
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleFleetLoading: false,
      fleetSize,
    })
  )
}

function vehicleFleetNotReceived(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleFleetLoading: false,
    })
  )
}

function settingServiceRecordModalVisibility(state, action) {
  const {
    payload: { visible, cancelHandler },
  } = action
  return state.merge({
    serviceRecordModal: {
      visible,
      cancelHandler,
    },
  })
}

function showRpPinAssignmentModal(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      rpPinAssignmentModalVisible: true,
    })
  )
}

function hideRpPinAssignmentModal(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      rpPinAssignmentModalVisible: false,
    })
  )
}

function requestingVehicleRevisions(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleRevisionsLoading: true,
    })
  )
}

function vehicleRevisionsReceived(state, action) {
  // The generic apiCall success action wraps the API response in action.data.
  // The API returns an array of revisions directly; fall back to action.revisions
  // for callers that still dispatch the old action shape directly.
  const { serialNumber } = action
  const revisions = action.data !== undefined ? action.data : action.revisions
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleRevisionsLoading: false,
      revisions: fromJS(revisions),
    })
  )
}

function vehicleRevisionsNotReceived(state, action) {
  const { serialNumber } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      vehicleRevisionsLoading: false,
      revisions: fromJS([]),
    })
  )
}

function getVehicleRevisions(state, action) {
  const { serialNumber, data } = action
  const trucks = state.get(TRUCK)

  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      revisions: fromJS(data),
    })
  )
}

function getParFileValidationData(state, action) {
  const { data, serialNumber, type, id, isPreProdRequest, ...validationData } =
    action
  // apiCall either puts the legacy dictionary in data or spreads it onto the
  // action. Preserve spread module keys while excluding action metadata.
  const responseData = data !== undefined ? data : validationData
  const trucks = state.get(TRUCK)
  return state.set(
    TRUCK,
    setDataOnTruck(trucks, serialNumber, {
      parFileValidation: fromJS(responseData),
    })
  )
}
